import { Button } from "@shared-ui/button";
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@shared-ui/select";
import { useState } from "react";
import { CategoryPicker } from "@/features/catalog/components/category-picker";
import type {
	AdminBrandSummary,
	BulkProductPatch,
} from "@/features/catalog/services/catalog-api.service";

/**
 * Every field the bulk endpoint accepts, at its full range.
 *
 * The bar beside this keeps three buttons -- publish, unpublish, set tier
 * premium -- because they are the actions an operator repeats, and a shortcut
 * for the common case is worth its screen space. What it does NOT do is bound
 * what is reachable: it covered two of the five fields the endpoint takes, and
 * neither at full range (tier offered only `premium` of three values, status
 * only two of three). Re-categorisation, the verb plan 008's V5 actually names,
 * had no control at all.
 *
 * Field-then-value rather than a button per value, because five fields at full
 * range is seventeen buttons.
 *
 * PERMISSIONS DISABLE, THEY DO NOT HIDE. That is what the sibling buttons
 * already do -- `canPublish` renders Publish disabled beside the sentence
 * "Publishing needs admin:catalog:publish" -- and it is the better behaviour: a
 * control that vanishes teaches an operator nothing, while a disabled one with
 * a reason tells them exactly which permission to ask for. The plan that
 * specified this unit said "hide, the canPublish pattern is the model"; the
 * pattern is disable, and a test pins it.
 */

/** The five fields, and what the server charges for each. */
const FIELDS = [
	{
		value: "categoryId",
		label: "Category",
		permission: "admin:catalog:write",
	},
	{ value: "tier", label: "Tier", permission: "admin:catalog:write" },
	{ value: "status", label: "Status", permission: "admin:catalog:publish" },
	{ value: "brandId", label: "Brand", permission: "admin:catalog:publish" },
	{
		value: "verification",
		label: "Verification",
		permission: "admin:catalog:verify",
	},
] as const;

type BulkField = (typeof FIELDS)[number]["value"];

const TIERS = ["standard", "premium", "luxury"] as const;
const STATUSES = ["draft", "active", "inactive"] as const;
/*
 * Kept in step with `verificationStatusEnum`. A value the server accepts and
 * this list omits is unreachable from the bar, which is the gap this unit
 * exists to close -- so the omission would be the same defect in a new place.
 */
const VERIFICATIONS = [
	"unverified",
	"auto_validated",
	"human_reviewed",
	"brand_confirmed",
] as const;

export function BulkChangeForm({
	brands,
	permissions,
	disabled,
	onApply,
}: {
	brands: AdminBrandSummary[];
	/** What the operator holds. Read per field, not once for the form. */
	permissions: ReadonlySet<string>;
	disabled?: boolean;
	/**
	 * `valueLabel` is the chosen value in words, where the patch carries only an
	 * id -- the category and brand pickers know the name and the page does not.
	 * Without it the confirmation says "Move to the chosen category", which is
	 * the sentence an operator cannot check against what they meant.
	 */
	onApply: (patch: BulkProductPatch, valueLabel?: string) => void;
}) {
	const [field, setField] = useState<BulkField>("categoryId");
	const [categoryId, setCategoryId] = useState<string | null>(null);
	const [categoryName, setCategoryName] = useState<string | null>(null);
	const [value, setValue] = useState<string>("");

	const chosen = FIELDS.find((entry) => entry.value === field);
	const permitted =
		chosen !== undefined &&
		(permissions.has(chosen.permission) || permissions.has("admin:*"));

	/** Whether the value control has something the server would accept. */
	const ready = field === "categoryId" ? categoryId !== null : value !== "";

	function apply(): void {
		if (!permitted || !ready) return;
		if (field === "categoryId" && categoryId) {
			onApply({ categoryId }, categoryName ?? undefined);
			return;
		}
		if (field === "tier") {
			onApply({ tier: value as BulkProductPatch["tier"] });
			return;
		}
		if (field === "status") {
			onApply({ status: value as BulkProductPatch["status"] });
			return;
		}
		if (field === "brandId") {
			onApply(
				{ brandId: value },
				brands.find((brand) => brand.id === value)?.name,
			);
			return;
		}
		onApply({ verification: value });
	}

	return (
		<div className="flex flex-wrap items-center gap-2">
			<Select
				value={field}
				onValueChange={(next) => {
					setField(next as BulkField);
					// The value control changes shape, so a value carried across from
					// the previous field would be sent for a field it does not belong to.
					setValue("");
					setCategoryId(null);
					setCategoryName(null);
				}}
			>
				{/* `aria-label`, because a Radix trigger takes its accessible name from
				    its VALUE -- the same reason the filter selects above carry one. */}
				<SelectTrigger className="w-[150px]" aria-label="Field to change">
					<SelectValue />
				</SelectTrigger>
				<SelectContent>
					<SelectGroup>
						{FIELDS.map((entry) => (
							<SelectItem key={entry.value} value={entry.value}>
								{entry.label}
							</SelectItem>
						))}
					</SelectGroup>
				</SelectContent>
			</Select>

			{field === "categoryId" ? (
				<div className="w-[220px]">
					<CategoryPicker
						id="bulk-category"
						label="New category"
						value={categoryId}
						valueLabel={categoryName}
						disabled={disabled || !permitted}
						onChange={(category) => {
							setCategoryId(category?.id ?? null);
							setCategoryName(category?.name ?? null);
						}}
					/>
				</div>
			) : (
				<Select
					value={value}
					onValueChange={setValue}
					disabled={disabled || !permitted}
				>
					<SelectTrigger className="w-[190px]" aria-label="New value">
						<SelectValue placeholder="Choose a value" />
					</SelectTrigger>
					<SelectContent>
						<SelectGroup>
							{field === "tier"
								? TIERS.map((tier) => (
										<SelectItem key={tier} value={tier} className="capitalize">
											{tier}
										</SelectItem>
									))
								: null}
							{field === "status"
								? STATUSES.map((entry) => (
										<SelectItem
											key={entry}
											value={entry}
											className="capitalize"
										>
											{entry}
										</SelectItem>
									))
								: null}
							{field === "verification"
								? VERIFICATIONS.map((entry) => (
										<SelectItem key={entry} value={entry}>
											{entry.replace(/_/g, " ")}
										</SelectItem>
									))
								: null}
							{field === "brandId"
								? brands.map((brand) => (
										<SelectItem key={brand.id} value={brand.id}>
											{brand.name}
										</SelectItem>
									))
								: null}
						</SelectGroup>
					</SelectContent>
				</Select>
			)}

			<Button
				variant="outline"
				size="sm"
				disabled={disabled || !permitted || !ready}
				onClick={apply}
			>
				Apply
			</Button>

			{/*
			 * The reason, named. A disabled control with no explanation is a dead end;
			 * with the permission spelled out it is something the operator can act on.
			 */}
			{!permitted && chosen ? (
				<span className="text-xs text-muted-foreground">
					Changing {chosen.label.toLowerCase()} needs {chosen.permission}.
				</span>
			) : null}
		</div>
	);
}
